<template>
  <div class="fp-wrap">

    <!-- Loading -->
    <div class="state-box" v-if="loadingData">
      <i class="fa fa-spinner fa-spin"></i> Loading…
    </div>

    <!-- Empty -->
    <div class="state-box" v-else-if="!loadingData && service_code_datas.length === 0">
      <i class="fa fa-fingerprint"></i> No service codes found
    </div>

    <template v-else>
      <!-- List header -->
      <div class="list-header">
        <div>Service</div>
        <div style="text-align:right;">Default Cost</div>
        <div style="text-align:right;">Custom Cost</div>
        <div style="text-align:center;">Status</div>
        <div></div>
      </div>

      <!-- Service rows -->
      <div
        v-for="(data, index) in service_code_datas"
        :key="index"
        class="sc-row"
        :class="{ 'is-expanded': !!expandedRows[index] }"
      >
        <!-- Collapsed row head -->
        <div class="sc-row-head" @click="toggleExpand(index)">
          <div class="srh-service">
            <div class="sc-icon"><i class="fa fa-fingerprint"></i></div>
            <span class="sc-name">{{ data.service_name }}</span>
          </div>
          <div class="srh-val">{{ data.default_cost ? ('$' + data.default_cost) : '—' }}</div>
          <div class="srh-val">
            <span v-if="data.sp_default_cost" class="val-custom">${{ data.sp_default_cost }}</span>
            <span v-else class="val-empty">—</span>
          </div>
          <div style="text-align:center;">
            <span v-if="hasCustom(data)" class="status-badge s-custom">
              <i class="fa fa-check-circle"></i> Custom
            </span>
            <span v-else class="status-badge s-default">Default</span>
          </div>
          <div class="srh-toggle">
            <i class="fa fa-chevron-down expand-icon" :class="{ rotated: expandedRows[index] }"></i>
          </div>
        </div>

        <!-- Expanded comparison panel -->
        <div class="sc-detail" v-if="expandedRows[index]">
          <div class="rate-compare">
            <div class="rc-header">
              <div class="rc-col">Field</div>
              <div><span class="rc-badge def"><i class="fa fa-lock"></i> Default</span></div>
              <div><span class="rc-badge cus"><i class="fa fa-edit"></i> Custom</span></div>
            </div>

            <div class="rc-row">
              <div class="rc-field">Cost</div>
              <div><span class="rc-def-val">${{ data.default_cost || '—' }}</span></div>
              <div>
                <div class="input-wrap">
                  <span class="ip">$</span>
                  <input class="rate-input" type="number" min="0" v-model="data.sp_default_cost" placeholder="0.00" />
                </div>
              </div>
            </div>

            <div class="rc-row">
              <div class="rc-field">Expedited Cost</div>
              <div><span class="rc-def-val">${{ data.default_expedited_cost || '—' }}</span></div>
              <div>
                <div class="input-wrap">
                  <span class="ip">$</span>
                  <input class="rate-input" type="number" min="0" v-model="data.sp_default_expedited_cost" placeholder="0.00" />
                </div>
              </div>
            </div>

            <div class="rc-row">
              <div class="rc-field">Expedited Markup</div>
              <div><span class="rc-def-val">${{ data.default_expedited_markup || '—' }}</span></div>
              <div>
                <div class="input-wrap">
                  <span class="ip">$</span>
                  <input class="rate-input" type="number" min="0" v-model="data.sp_default_expedited_markup" placeholder="0.00" />
                </div>
              </div>
            </div>

            <div class="rc-row">
              <div class="rc-field">Tax</div>
              <div><span class="rc-def-val">{{ data.default_tax || '—' }}%</span></div>
              <div>
                <div class="input-wrap">
                  <span class="ip">%</span>
                  <input class="rate-input" type="number" min="0" v-model="data.sp_default_tax" placeholder="0" />
                </div>
              </div>
            </div>

            <div class="rc-row">
              <div class="rc-field">Markup</div>
              <div><span class="rc-def-val">${{ data.default_markup || '—' }}</span></div>
              <div>
                <div class="input-wrap">
                  <span class="ip">$</span>
                  <input class="rate-input" type="number" min="0" v-model="data.sp_default_markup" placeholder="0.00" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Save bar -->
    <div class="save-bar" v-if="!loadingData && service_code_datas.length > 0">
      <div class="save-bar__inner">
        <span class="save-bar__info">
          {{ client.name ? client.name + ' — Service Code Rates' : 'Service Code Rates' }}
        </span>
        <button class="btn-save" ref="kt_save_sp_sc_rates" @click="update('S', 'kt_save_sp_sc_rates')">
          <i class="fa fa-check"></i> Save Rates
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import {
  FETCH_SERVICECODES,
  FETCH_SERVICECODES_BY_SP,
  UPDATE_SERVICECODES_BY_SP
} from "@/core/services/store/actions.type";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      service_code_datas: [],
      client: {},
      errors: [],
      loadingData: false,
      serviceError: "",
      expandedRows: {}
    };
  },
  computed: {
    ...mapGetters(["getServiceCodeData"])
  },
  created() {
    this.getServiceCode();
  },
  methods: {
    toggleExpand(index) {
      this.$set(this.expandedRows, index, !this.expandedRows[index]);
    },
    hasCustom(data) {
      return data.sp_default_cost || data.sp_default_markup || data.sp_default_tax;
    },
    getServiceCode() {
      this.loadingData = true;
      this.$store.dispatch(FETCH_SERVICECODES)
        .then(response => {
          this.$store.dispatch(FETCH_SERVICECODES_BY_SP, this.$route.params.id)
            .then(response1 => {
              this.service_code_datas = response.data;
              if (response1.data && response1.data.length > 0) {
                response1.data.forEach(el => {
                  const idx = this.service_code_datas.findIndex(x => el.service_code === x.id);
                  if (idx !== -1) {
                    this.service_code_datas[idx] = {
                      ...this.service_code_datas[idx],
                      sp_default_cost:             el.default_cost,
                      sp_default_tax:              el.default_tax,
                      sp_default_markup:           el.default_markup,
                      sp_default_expedited_cost:   el.default_expedited_cost,
                      sp_default_expedited_markup: el.default_expedited_markup
                    };
                  }
                });
              }
              this.loadingData = false;
            })
            .catch(() => { this.loadingData = false; });
        })
        .catch(() => { this.loadingData = false; });
    },
    closeBtnLoad(ref) { const b = this.$refs[ref]; if (b) b.disabled = false; },
    loadingButton(ref) { const b = this.$refs[ref]; if (b) b.disabled = true; },
    update(_saveType, ref) {
      this.loadingButton(ref);
      const payload = this.service_code_datas.map(el => ({
        service_code_id:            el.id,
        client_id:                  this.$route.params.id,
        default_cost:               el.sp_default_cost               ?? "",
        default_tax:                el.sp_default_tax                ?? "",
        default_markup:             el.sp_default_markup             ?? "",
        default_expedited_cost:     el.sp_default_expedited_cost     ?? "",
        default_expedited_markup:   el.sp_default_expedited_markup   ?? ""
      }));
      this.$store.dispatch(UPDATE_SERVICECODES_BY_SP, payload)
        .then(data => { this.$toastr.s(data.msg); this.closeBtnLoad(ref); })
        .catch(err => { this.errors = err; this.serviceError = ""; this.getServiceCode(); this.closeBtnLoad(ref); });
    }
  }
};
</script>

<style lang="scss" scoped>
$font:  'Segoe UI', sans-serif;
$blue:  #389ad4;
$dark:  #0d1b3e;
$green: #276749;
$border: #dde2ec;

/* ── List header ── */
.list-header {
  display: grid;
  grid-template-columns: 1fr 130px 130px 110px 44px;
  align-items: center;
  padding: 8px 20px;
  background: #fafbfd;
  border-bottom: 2px solid #eef0f8;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #a0aab8;
  gap: 8px;
}

/* ── Service row ── */
.sc-row {
  border-bottom: 1px solid #f5f7fa;

  &:last-child { border-bottom: none; }

  &.is-expanded > .sc-row-head {
    background: #f8fbff;
    border-bottom: 1px solid #e8edf5;
  }
}

.sc-row-head {
  display: grid;
  grid-template-columns: 1fr 130px 130px 110px 44px;
  align-items: center;
  padding: 11px 20px;
  cursor: pointer;
  gap: 8px;
  transition: background 0.12s;

  &:hover { background: #f8fbff; }
}

.srh-service {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;

  .sc-icon {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: #eef2fb;
    color: #185FA5;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
  }

  .sc-name {
    font-size: 13px;
    font-weight: 600;
    color: $dark;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.srh-val {
  text-align: right;
  font-size: 12.5px;
  font-family: monospace;
  font-weight: 600;
  color: #4a5568;

  .val-custom { color: $green; }
  .val-empty  { color: #c0c7d4; font-weight: 400; }
}

.srh-toggle {
  display: flex;
  justify-content: center;

  .expand-icon {
    font-size: 11px;
    color: #a0aab8;
    transition: transform 0.2s;

    &.rotated { transform: rotate(180deg); }
  }
}

/* ── Status badges ── */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  &.s-custom  { background: #dcfce7; color: $green; }
  &.s-default { background: #e2e8f0; color: #718096; }
}

/* ── Expanded panel ── */
.sc-detail {
  background: #f9fbfd;
  border-top: 1px solid #eef0f8;
  padding: 14px 20px 14px 57px;
}

.rate-compare {
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 10px;
  overflow: hidden;
  max-width: 580px;
}

.rc-header {
  display: grid;
  grid-template-columns: 1fr 140px 180px;
  padding: 8px 16px;
  background: #f5f8fc;
  border-bottom: 1px solid #e8edf5;
  gap: 12px;

  .rc-col {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #a0aab8;
  }

  .rc-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 9px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;

    &.def { background: #dbeafe; color: #1d4ed8; }
    &.cus { background: #dcfce7; color: $green; }
  }
}

.rc-row {
  display: grid;
  grid-template-columns: 1fr 140px 180px;
  align-items: center;
  padding: 9px 16px;
  border-bottom: 1px solid #f5f7fa;
  gap: 12px;

  &:last-child { border-bottom: none; }
  &:hover { background: #fafcff; }

  .rc-field {
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
  }

  .rc-def-val {
    font-size: 12px;
    font-family: monospace;
    font-weight: 600;
    color: #7a8499;
    padding: 4px 10px;
    background: #f4f8fe;
    border-radius: 5px;
    display: inline-block;
  }
}

/* ── Input ── */
.input-wrap {
  display: flex;
  align-items: center;
  height: 30px;
  border: 1.5px solid $border;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.15s;

  &:focus-within { border-color: $blue; box-shadow: 0 0 0 2px rgba(56,154,212,0.1); }

  .ip {
    height: 100%;
    padding: 0 6px 0 8px;
    background: #f5f7fa;
    border-right: 1px solid #e2e8f0;
    color: #a0aab8;
    font-size: 11px;
    font-weight: 600;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    user-select: none;
  }

  .rate-input {
    flex: 1;
    border: none;
    outline: none;
    height: 100%;
    padding: 0 8px;
    font-size: 12.5px;
    color: $dark;
    font-family: $font;
    background: transparent;
    text-align: right;
    min-width: 0;

    &[type=number] { -moz-appearance: textfield; appearance: textfield; }
    &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  }
}

/* ── States ── */
.state-box {
  text-align: center;
  padding: 56px 24px;
  color: #a0aab8;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* ── Save bar ── */
.save-bar {
  position: fixed;
  bottom: 2.5rem;
  width: calc(78vw);
  z-index: 200;

  .save-bar__inner {
    background: $dark;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(13,27,62,0.3);
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .save-bar__info {
      font-size: 13px;
      color: rgba(255,255,255,0.75);
      font-family: $font;
    }

    .btn-save {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      height: 36px;
      padding: 0 22px;
      background: $green;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      font-family: $font;
      cursor: pointer;
      transition: opacity 0.2s;

      &:hover { opacity: 0.9; }
      &:disabled { opacity: 0.55; cursor: not-allowed; }
    }
  }
}
</style>
